import React from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { getProjects, getUserProjects, updateUserProjects } from '../api';

interface Project {
  id: number;
  name: string;
  description?: string;
  is_active?: boolean;
}

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  user?: any;
  onSubmit: (data: any) => void;
}

export const UserForm: React.FC<UserFormProps> = ({ open, onClose, user, onSubmit }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = React.useState({
    email: user?.email || '',
    password: '',
    role: user?.role || 'MANAGER'
  });
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [selectedProjects, setSelectedProjects] = React.useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        password: '',
        role: user.role || 'MANAGER'
      });
    }
  }, [user]);

  const loadProjects = React.useCallback(async () => {
    try {
      setLoadingProjects(true);
      const response = await getProjects();
      setProjects(response || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const loadUserProjects = React.useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await getUserProjects(user.id);
      const userProjects = response.data || [];
      setSelectedProjects(userProjects);
    } catch (error) {
      console.error('Error loading user projects:', error);
    }
  }, [user?.id]);

  // Загружаем список проектов при открытии формы
  React.useEffect(() => {
    if (open) {
      void loadProjects();
      if (user?.id) {
        void loadUserProjects();
      }
    }
  }, [open, user?.id, loadProjects, loadUserProjects]);

  const handleSubmit = async () => {
    try {
      // Подготавливаем данные для отправки - используем email как userId и operatorName
      const submitData = {
        userId: formData.email,
        operatorName: formData.email,
        email: formData.email,
        password: formData.password,
        role: formData.role
      };
      
      // Создаем/обновляем пользователя и получаем результат
      const result = await onSubmit(submitData);
      
      // Определяем ID пользователя (либо из существующего пользователя, либо из результата создания)
      const userId = user?.id || (result as any)?.id;
      
      // Обновляем проекты пользователя, если у нас есть ID
      if (userId && selectedProjects.length >= 0) {
        const projectIds = selectedProjects.map(p => p.id);
        await updateUserProjects(userId, projectIds);
      }
      
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{user ? t('edit') || 'Edit' : t('create') || 'Create'} {t('user') || 'User'}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={t('email') || 'Email'}
          value={formData.email}
          onChange={e => setFormData({ ...formData, email: e.target.value })}
          margin="normal"
          required
          type="email"
        />
        <TextField
          fullWidth
          label={t('password') || 'Password'}
          value={formData.password}
          onChange={e => setFormData({ ...formData, password: e.target.value })}
          margin="normal"
          required={!user}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          select
          label={t('role') || 'Role'}
          value={formData.role}
          onChange={e => setFormData({ ...formData, role: e.target.value })}
          margin="normal"
        >
          <MenuItem value="ADMIN">ADMIN</MenuItem>
          <MenuItem value="MANAGER">MANAGER</MenuItem>
          <MenuItem value="CLIENT">CLIENT</MenuItem>
        </TextField>

        {/* Множественный выбор проектов */}
        <Autocomplete
          multiple
          options={projects}
          getOptionLabel={(option) => option.name}
          value={selectedProjects}
          onChange={(event, newValue) => {
            setSelectedProjects(newValue);
          }}
          loading={loadingProjects}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  variant="outlined"
                  label={option.name}
                  {...tagProps}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={t('projects') || 'Projects'}
              placeholder={t('select_projects') || 'Select projects'}
              margin="normal"
              fullWidth
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel') || 'Cancel'}</Button>
        <Button onClick={handleSubmit} variant="contained">{t('submit') || 'Submit'}</Button>
      </DialogActions>
    </Dialog>
  );
}; 